import { Drawer, List, Switch, Typography, Flex, Space } from 'antd';
import { useIntl } from 'react-intl';
import { FC, useState } from 'react';
import { ValidationMethod, ValidationType } from 'entities/user/model/types';
import { useSelector } from 'react-redux';
import { getUserMeta } from 'shared/config/store/reducers/userSlice';
import { useLoginActions, useConfirmationActions } from '../api/actions';
import { useDispatch } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { handleErrorWithCommonFallback } from 'shared/libs/utils/helpers';

import { TwoFactorUpdate } from './TwoFactorUpdate';
import { UpdateParams } from '../model/types';
import { BackButton } from 'shared/ui';
import classes from './TwoFactorDrawer.module.css';
import { ArrowRightOutlined } from '@ant-design/icons';

type Props = {
  open: boolean;
  onClose: () => void;
};

type View = 'main' | 'login' | 'confirmation';

const TwoFactorDrawer: FC<Props> = ({ open, onClose }) => {
  const { formatMessage: t } = useIntl();
  const userInfo = useSelector(getUserMeta);
  const dispatch = useDispatch();
  const { showError } = useMessage();
  const loginActions = useLoginActions();
  const confirmationActions = useConfirmationActions();
  
  const [currentView, setCurrentView] = useState<View>('main');
  const [updateParams, setUpdateParams] = useState<UpdateParams | undefined>(undefined);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  const { validateLogin, validateConfirmation, email } = userInfo;

  const MAPPED_METHOD_TRANSLATE: Record<ValidationMethod, string> = {
    [ValidationMethod.No]: 'screens.validation.type.no',
    [ValidationMethod.SMS]: 'screens.validation.type.sms',
    [ValidationMethod.TwoFA]: 'screens.validation.type.2fa',
    [ValidationMethod.Biometry]: 'screens.validation.type.biometry',
  };

  const onOpenUpdate = (params: UpdateParams) => {
    setUpdateParams(params);
    setIsUpdateOpen(true);
  };

  const onCloseUpdate = () => {
    setUpdateParams(undefined);
    setIsUpdateOpen(false);
  };

  const onToggleLoginNo = async () => {
    dispatch(setActivity(true));
    try {
      const result = await loginActions.enableNo();
      const { id, property1, status } = result.response;
      onOpenUpdate({
        id: id || '',
        method: ValidationMethod.No,
        status,
        secret: property1,
        type: ValidationType.Login,
      });
    } catch (error: any) {
      handleErrorWithCommonFallback(error, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onToggleLoginTwoFactor = async () => {
    dispatch(setActivity(true));
    try {
      const result = await loginActions.enableTwoFactor();
      const { id, property1, status } = result.response;
      onOpenUpdate({
        id: id || '',
        method: ValidationMethod.TwoFA,
        status,
        secret: property1,
        type: ValidationType.Login,
      });
    } catch (error: any) {
      handleErrorWithCommonFallback(error, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onToggleConfirmationSms = async () => {
    dispatch(setActivity(true));
    try {
      const result = await confirmationActions.enableSms();
      const { id, property1, status } = result.response;
      onOpenUpdate({
        id: id || '',
        method: ValidationMethod.SMS,
        status,
        secret: property1,
        type: ValidationType.Confirmation,
      });
    } catch (error: any) {
      handleErrorWithCommonFallback(error, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onToggleConfirmationTwoFactor = async () => {
    dispatch(setActivity(true));
    try {
      const result = await confirmationActions.enableTwoFactor();
      const { id, property1, status } = result.response;
      onOpenUpdate({
        id: id || '',
        method: ValidationMethod.TwoFA,
        status,
        secret: property1,
        type: ValidationType.Confirmation,
      });
    } catch (error: any) {
      handleErrorWithCommonFallback(error, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  const renderMainView = () => (
    <List
      itemLayout="horizontal"
      dataSource={[
        {
          title: t({ id: 'screens.validation.login.title' }),
          subtitle: validateLogin ? t({ id: MAPPED_METHOD_TRANSLATE[validateLogin] }) : '',
          onClick: () => setCurrentView('login'),
        },
        // DEV-6468: Remove confirmation view while no operations
        // {
        //   title: t({ id: 'screens.validation.confirmation.title' }),
        //   subtitle: validateConfirmation ? t({ id: MAPPED_METHOD_TRANSLATE[validateConfirmation] }) : '',
        //   onClick: () => setCurrentView('confirmation'),
        // },
      ]}
      split={false}
      renderItem={(item) => (
        <List.Item className={classes.mainItem} onClick={item.onClick}>
          <Flex justify="space-between" align="center" className="w100">
            <Flex vertical gap={4}>
              <Typography.Title level={5}>{item.title}</Typography.Title>
              {item.subtitle && <Typography.Text className={classes.subtext}>{item.subtitle}</Typography.Text>}
            </Flex>
            <ArrowRightOutlined />
          </Flex>
        </List.Item>
      )}
    />
  );

  const renderLoginView = () => (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <BackButton onClick={() => setCurrentView('main')} style={{ padding: 0 }} />
      <Typography.Title level={5} >
        {t({ id: 'screens.validation.list.title' })}
      </Typography.Title>
      <List
        bordered
        dataSource={[
          {
            title: t({ id: 'screens.validation.type.no' }),
            checked: validateLogin === ValidationMethod.No,
            onClick: onToggleLoginNo,
          },
          {
            title: t({ id: 'screens.validation.type.2fa' }),
            checked: validateLogin === ValidationMethod.TwoFA,
            onClick: onToggleLoginTwoFactor,
          },
        ]}
        renderItem={(item) => (
          <List.Item>
            <Flex justify="space-between" align="center" className="w100">
              <Typography.Text className={classes.text}>{item.title}</Typography.Text>
              <Switch value={item.checked} onClick={item.onClick} />
            </Flex>
          </List.Item>
        )}
      />
    </Space>
  );

  const renderConfirmationView = () => {
    const isBiometryEnabled = validateConfirmation === ValidationMethod.Biometry;
    
    const confirmationItems = [
      {
        title: t({ id: 'screens.validation.type.sms' }),
        checked: validateConfirmation === ValidationMethod.SMS,
        disabled: isBiometryEnabled,
        onClick: onToggleConfirmationSms,
      },
      {
        title: t({ id: 'screens.validation.type.2fa' }),
        checked: validateConfirmation === ValidationMethod.TwoFA,
        disabled: isBiometryEnabled,
        onClick: onToggleConfirmationTwoFactor,
      },
    ];

    if (isBiometryEnabled) {
      confirmationItems.push({
        title: t({ id: 'screens.validation.type.biometry' }),
        checked: true,
        disabled: true,
        onClick: async () => {},
      });
    }
    
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <BackButton onClick={() => setCurrentView('main')} style={{ padding: 0 }} />
        
        <Typography.Title level={5} >
          {t({ id: 'screens.validation.list.title' })}
        </Typography.Title>
        
        <List
          bordered
          dataSource={confirmationItems}
          renderItem={(item) => (
            <List.Item>
              <Flex justify="space-between" align="center" className="w100">
                <Typography.Text className={classes.text}>{item.title}</Typography.Text>
                <Switch 
                  value={item.checked} 
                  disabled={item.disabled}
                  onClick={item.onClick} 
                />
              </Flex>
            </List.Item>
          )}
        />
        
        {isBiometryEnabled && (
          <Flex className={classes.warningContainer}>
            <Typography.Text className={classes.warningText}>
              ⚠ {t({ id: 'screens.validation.list.message.mobile' })}
            </Typography.Text>
          </Flex>
        )}
      </Space>
    );
  };

  const getTitle = () => {
    switch (currentView) {
      case 'login':
        return t({ id: 'screens.validation.login.title' });
      case 'confirmation':
        return t({ id: 'screens.validation.confirmation.title' });
      default:
        return t({ id: 'screens.validation.title' });
    }
  };

  const renderContent = () => {
    switch (currentView) {
      case 'login':
        return renderLoginView();
      case 'confirmation':
        return renderConfirmationView();
      default:
        return renderMainView();
    }
  };

  return (
    <>
      <Drawer
        title={getTitle()}
        placement="right"
        width={560}
        onClose={onClose}
        open={open}>
        {renderContent()}
      </Drawer>
      <TwoFactorUpdate
        isOpen={isUpdateOpen}
        email={email || ''}
        onClose={onCloseUpdate}
        updateParams={updateParams}
      />
    </>
  );
};

export default TwoFactorDrawer; 